import 'src/global.css';

import { useEffect } from 'react';

import { usePathname } from 'src/routes/hooks';

import { themeConfig, ThemeProvider } from 'src/theme';

import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';

import { AuthProvider } from 'src/auth/context/jwt';

import { Snackbar } from './components/snackbar';
import { LocalizationProvider } from './locales';
import { ChannelProvider } from './pages/channel/hooks';
import { PremissionProvider } from './pages/auth/hooks';
import { CampaignProvider } from './pages/campaign/hooks';
import { CallPopup } from './pages/chat/components/call-popup';
import { CallDialog } from './pages/chat/components/call-dialog';

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

export default function App({ children }: AppProps) {
  useScrollToTop();

  return (
    <AuthProvider>
      <PremissionProvider>
        <SettingsProvider defaultSettings={defaultSettings}>
          <LocalizationProvider>
            <ThemeProvider
              noSsr
              defaultMode={themeConfig.defaultMode}
              modeStorageKey={themeConfig.classesPrefix}
            >
              <MotionLazy>
                <CampaignProvider>
                  <ChannelProvider>
                    <Snackbar />
                    <ProgressBar />
                    <CallDialog />
                    <CallPopup />
                    <SettingsDrawer defaultSettings={defaultSettings} />
                    {children}
                  </ChannelProvider>
                </CampaignProvider>
              </MotionLazy>
            </ThemeProvider>
          </LocalizationProvider>
        </SettingsProvider>
      </PremissionProvider>
    </AuthProvider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
